import { trackClarityEvent } from './clarity-event.util';

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

/** Primitive props safe for Clarity + GTM dataLayer. */
export type ProductAnalyticsProps = Record<
  string,
  string | number | boolean | null | undefined
>;

/**
 * Flatten unknown payloads into GTM/Clarity-safe primitives.
 * Drops objects/arrays so Tag Manager variable mapping stays simple.
 */
export function toProductAnalyticsProps(
  payload?: Record<string, unknown>
): ProductAnalyticsProps {
  const out: ProductAnalyticsProps = {};
  if (!payload) return out;
  Object.entries(payload).forEach(([key, value]) => {
    if (value === null) {
      out[key] = null;
    } else if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      out[key] = value;
    }
  });
  return out;
}

/**
 * Push a custom event into GTM's dataLayer (no-op if unavailable).
 * GTM should listen for event = `ss_product` and map `ss_event` → GA4 event name.
 */
export function trackGtmEvent(
  eventName: string,
  properties?: ProductAnalyticsProps
): void {
  try {
    if (typeof window === 'undefined' || !eventName) return;
    window.dataLayer = window.dataLayer || [];
    const row: Record<string, unknown> = {
      event: 'ss_product',
      ss_event: eventName,
    };
    Object.entries(properties || {}).forEach(([key, value]) => {
      if (value !== undefined) {
        row[`ss_${key}`] = value;
      }
    });
    window.dataLayer.push(row);
  } catch {
    /* GTM is optional */
  }
}

/**
 * One call → Clarity custom event + GTM dataLayer.
 * Prefer this for activation / product funnel milestones.
 */
export function trackProductEvent(
  eventName: string,
  properties?: Record<string, unknown>
): void {
  if (!eventName) return;
  const props = toProductAnalyticsProps(properties);
  try {
    trackClarityEvent(eventName, props);
  } catch {
    /* Clarity optional */
  }
  try {
    trackGtmEvent(eventName, props);
  } catch {
    /* GTM optional */
  }
}
